import { SelectOption } from "components/dropdown/Dropdown";
import { FormikContextType, useField, useFormikContext } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import api from "api";
import Select from "react-select";
import { ValueType } from "react-select/src/types";
import {
  CustomControl,
  RolesCustomOptionWithLink,
  CustomValue,
  SelectConfig,
  SelectConfigOptionalSearchParameters,
  customUseOnClickOutside,
} from "./SearchListRenderer";
import { debounce } from "lodash";
import { useOnceAfterCondition } from "../../../../helpers/useOnceAfterCondition";
import { useGetPermissions } from "../../../../helpers/useGetPermissions";

import _ from "lodash";

type MultiSelectSearchListWithLinksProps = SelectConfig & {
  name: string;
  dataSource: string | null;
  singleSelect?: boolean;
  label?: string;
  expandable?: boolean;
  optionalSearchParameters?: SelectConfigOptionalSearchParameters;
  callSearchEndpointOnClick?: boolean;
};

export type OptionalSearchParameters = { [parameterName: string]: any };

const getOptionalSearchParameters = (
  optionalSearchParameters: SelectConfigOptionalSearchParameters | undefined,
  formik: FormikContextType<any>,
  name: string
) => {
  let parsedOptionalSearchParameters: OptionalSearchParameters | null = null;
  if (
    optionalSearchParameters &&
    typeof optionalSearchParameters === "object" &&
    optionalSearchParameters.hasOwnProperty(name) &&
    !parsedOptionalSearchParameters
  ) {
    const data = optionalSearchParameters[name];

    const tempParsedOptionalSearchParameters: OptionalSearchParameters = {};

    Object.keys(data).forEach((key: string) => {
      const valueKey = data[key];

      if (formik.values && formik.values.hasOwnProperty(valueKey)) {
        const value = formik.values[valueKey as keyof typeof formik.values];

        tempParsedOptionalSearchParameters[key] = value;
      }
    });

    if (Object.keys(tempParsedOptionalSearchParameters).length > 0) {
      parsedOptionalSearchParameters = tempParsedOptionalSearchParameters;
    }
    return parsedOptionalSearchParameters;
  }
};

export const RolesMultiSelectSearchListWithLinks: React.FC<MultiSelectSearchListWithLinksProps> = ({
  name,
  label,
  dataSource,
  searchEndpoint,
  handleActionLinkClicked,
  onLinkClick,
  actionLinkConfig,
  linkTextConfig,
  singleSelect = false,
  expandable,
  optionalSearchParameters,
  callSearchEndpointOnClick,
}) => {
  //set search endpoint
  const [parsedSearchEndpoint, setParsedSearchEndpoint] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (searchEndpoint) {
      if (
        typeof searchEndpoint === "object" &&
        searchEndpoint.hasOwnProperty(name)
      ) {
        setParsedSearchEndpoint(
          searchEndpoint[name as keyof typeof searchEndpoint]
        );
      } else if (typeof searchEndpoint === "string") {
        setParsedSearchEndpoint(searchEndpoint);
      }
    }
  }, [searchEndpoint]);

  const [{ value }, { touched, error, initialValue }, { setValue }] = useField<
    number[] | null
  >(name);

  const {
    values: formValues,
    setTouched,
    touched: formikTouched,
    setFieldValue,
    submitCount,
  } = useFormikContext<{
    [key: string]: any;
  }>();

  const hasError = submitCount > 0 && error;

  const [options, setOptions] = useState<SelectOption[] | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[] | null>(
    null
  );

  const defaultOptions = formValues[dataSource as keyof typeof formValues];

  const setExistingValues = () => {
    if (singleSelect) {
      if (initialValue) {
        const startOps = options?.filter(opt => initialValue === opt.value);

        setSelectedOptions(startOps ?? []);
        setFieldValue(name, initialValue);
      }
    }

    if (!value) return;

    if (!Array.isArray(value)) {
      const startOps = options?.filter(opt => value === opt.value);

      setSelectedOptions(startOps ?? []);
    } else if (Array.isArray(value) && value.length > 0) {
      const startOps = options?.filter(opt => value.includes(opt.value));

      setSelectedOptions(startOps ?? []);
    }
  };

  useOnceAfterCondition(
    () => {
      setExistingValues();
    },
    [options],
    options
  );

  const setDefaultOpts = () => {
    if (
      defaultOptions &&
      defaultOptions.length >= 1 &&
      defaultOptions[0].hasOwnProperty("label") &&
      defaultOptions[0].hasOwnProperty("value")
    ) {
      setOptions(defaultOptions);
    } else {
      setOptions([]);
    }
  };
  useEffect(() => {
    setDefaultOpts();
  }, []);
  //control search input
  const [searchString, setSearchString] = useState("");
  //control dropdown menu
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const bind = customUseOnClickOutside(() => setMenuOpen(false), true);

  const formik = useFormikContext();

  useEffect(() => {
    setMenuOpen(false);
  }, [selectedOptions]);

  const filterPlaceholder = (option: any, rawInput: string) => {
    return true;
  };

  const onChange = (v: SelectOption[] | SelectOption) => {
    let newOptions: any = v;

    let optionsToAdd: any = v;

    if (Array.isArray(newOptions)) {
      setSelectedOptions(newOptions);
    } else {
      setSelectedOptions([newOptions]);

      optionsToAdd = [optionsToAdd];
    }

    if (singleSelect) {
      if (Array.isArray(v) && v.length > 0) {
        setValue(v[0].value);
        setFieldValue(name, v[0].value);
      } else if (typeof v === "object" && v.hasOwnProperty("value")) {
        setValue([v["value" as keyof typeof v]]);
        setFieldValue(name, v["value" as keyof typeof v]);
      } else {
        setValue(null);
        setFieldValue(name, null);
      }
    } else {
      if (Array.isArray(v) && v.length > 0) {
        let finalValues = v.map(val => val.value);
        setValue(finalValues);
        setFieldValue(name, finalValues);
      } else {
        setValue(null);
        setFieldValue(name, null);
      }
    }
  };

  //control search
  const fetchResults = async (
    searchString: string,
    parsedSearchEndpoint: string,
    existingValues?: SelectOption[]
  ) => {
    if (parsedSearchEndpoint) {
      let parameters: any = { searchString };

      const parsedOptionalSearchParameters = getOptionalSearchParameters(
        optionalSearchParameters,
        formik,
        name
      );

      if (parsedOptionalSearchParameters) {
        parameters = { ...parameters, ...parsedOptionalSearchParameters };
      }

      try {
        const results = await api.get(parsedSearchEndpoint, {
          params: parameters,
        });
        let options: SelectOption[] = [];

        if (
          results &&
          results.data &&
          results.data.status === "1" &&
          results.data.details.results &&
          Array.isArray(results.data.details.results)
        ) {
          options = results.data.details.results;
        }

        if (searchString === "" && existingValues) {
          const existingIds = existingValues.map(item => item.value);

          const tempExistingValuesInSearchResults = options.filter(item =>
            existingIds.includes(item.value)
          );

          options = [...options, ...tempExistingValuesInSearchResults];

          let newSelectedOptions = selectedOptions
            ? [...selectedOptions, ...tempExistingValuesInSearchResults]
            : [...tempExistingValuesInSearchResults];

          setSelectedOptions(newSelectedOptions);

          let newFormSelectedOptions = selectedOptions
            ? [
                ...selectedOptions,
                tempExistingValuesInSearchResults.map(item => item.value),
              ]
            : [...tempExistingValuesInSearchResults.map(item => item.value)];

          formik.setFieldValue(name, newFormSelectedOptions);
        }

        setOptions(options);
      } catch (error) {
        // Error case
        console.log("fetchResults error", error);
      }
    }
  };

  const getSearchResults = useCallback(debounce(fetchResults, 500), []);

  const onSearch = (e: any) => {
    e.preventDefault();
    if (!e.target.value) {
      setSearchString("");
      setDefaultOpts();

      if (parsedSearchEndpoint) {
        getSearchResults("", parsedSearchEndpoint);
      }
    } else if (parsedSearchEndpoint && e.target.value) {
      setSearchString(e.target.value);
      getSearchResults(e.target.value, parsedSearchEndpoint);
    }
  };

  //if no endpoint filter meta list
  const onFilter = (e: any) => {
    e.preventDefault();
    let inputString = e.target.value;
    if (e.target.value === "") {
      setSearchString("");
      setDefaultOpts();
    } else if (
      defaultOptions &&
      defaultOptions.length >= 1 &&
      defaultOptions[0].hasOwnProperty("label") &&
      defaultOptions[0].hasOwnProperty("value")
    ) {
      setSearchString(inputString);
      setOptions(
        defaultOptions.filter((opt: SelectOption) =>
          opt.label.toLowerCase().includes(inputString.toLowerCase())
        ) ?? null
      );
    } else {
      setSearchString(e.target.value);
    }
  };

  //control links
  const handleAction = () => {
    handleActionLinkClicked && handleActionLinkClicked(name);
  };
  const action = actionLinkConfig ? actionLinkConfig[name] : null;
  const link = linkTextConfig ? linkTextConfig[name] : null;
  const actionText = action ? action.text : "";
  const permissions = action
    ? useGetPermissions([action.permissionKey], action.location)
    : false;
  const linkPermissions = link
    ? useGetPermissions([link.permissionKey], link.location)
    : false;
  let hasPermission =
    permissions && action && permissions.get(action.permissionKey);
  let linkHasPermission =
    linkPermissions && link && linkPermissions.get(link.permissionKey);
  const linkTextStr = linkHasPermission && link ? link.text : "";

  const selectedOptionEditDisabled = selectedOptions
    ? selectedOptions
        .map(item => (item && item.label ? item.label : null))
        .join(", ")
        .trim()
    : null;

  return (
    <>
      <div className={`FormLabel MultiSelect `}>
        <label>{label}</label>
      </div>
      <div className="FormField">
        <div
          className={`EditSingleSelectFieldContainer SearchListRenderer RolesSearchList ${
            hasPermission && handleActionLinkClicked ? "WithLinks" : ""
          } ${name}`}
          key={`MultiSelectSearchRenderer-${name}`}
          {...bind}
        >
          <Select
            className={`CustomSelect EditSingleSelect SearchListRenderer ${
              singleSelect ? "Single" : ""
            }`}
            classNamePrefix={`CustomSelect${hasError ? "Error" : ""}`}
            options={options ?? []}
            value={selectedOptions}
            isSearchable={false}
            placeholder={false}
            onChange={
              onChange as (selectedOption: ValueType<SelectOption, any>) => void
            }
            selectProps={{
              fieldName: name,
              onLinkClick: onLinkClick,
              linkText: linkTextStr,
              menuOpen: menuOpen,
              setMenuOpen: setMenuOpen,
              multiSelect: true,
              selectedOptions: selectedOptions,
              extraInfo: options,
              searchString,
              parsedSearchEndpoint,
              fetchResults,
              callSearchEndpointOnClick,
            }}
            noOptionsMessage={() => "No options"}
            menuIsOpen={menuOpen}
            onMenuOpen={() => {
              setMenuOpen(true); // NOTE: This is needed to make click on mobile phones work
            }}
            onMenuClose={() => {
              setMenuOpen(false); // NOTE: This is needed to make click on mobile phones work
            }}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option: RolesCustomOptionWithLink,
              Control: CustomControl,
            }}
            isMulti={!singleSelect}
            key={`MultiSelectSearch-${name}`}
            filterOption={parsedSearchEndpoint ? filterPlaceholder : undefined}
            onBlur={() => {
              let newTouched = { ...formikTouched };
              newTouched[name] = true;
              setTouched(newTouched);
            }}
            backspaceRemovesValue={false} // Prevent backspace key presses deleting selected items
            styles={{
              multiValue: (styles, { data }) => {
                return {
                  ...styles,
                  backgroundColor: "#DEEBFF",
                  color: "#4C9AFF",
                };
              },
              multiValueLabel: (styles, { data }) => ({
                ...styles,
                color: "#4C9AFF",
                fontSize: "1.1rem",
              }),
              multiValueRemove: (styles, { data }) => ({
                ...styles,
                color: "#4C9AFF",
                ":hover": {
                  backgroundColor: "red",
                  color: "white",
                },
              }),
            }}
          />
          {/*search bar overlay*/}
          <div
            className={`${menuOpen ? "behind" : "hide"} ${
              hasPermission && handleActionLinkClicked ? "WithLinks" : ""
            }`}
          >
            <input
              className="custom-input-search"
              value={searchString}
              onChange={parsedSearchEndpoint ? onSearch : onFilter}
              placeholder="Type search here"
              onFocus={() => {
                let newTouched = { ...formikTouched };
                newTouched[name] = false;
                setTouched(newTouched);
              }}
              onBlur={() => {
                let newTouched = { ...formikTouched };
                newTouched[name] = true;
                setTimeout(() => {
                  setTouched(newTouched);
                }, 100);
              }}
            />

            {hasPermission && handleAction && (
              <div onClick={handleAction} className="Link">
                {actionText ?? "Action"}
              </div>
            )}
            <div className="info">
              * Highlighted users have a role assigned. If selected, the role
              will be replaced along with all access rights associated with it.
            </div>
          </div>
          {/*formik error */}
          {hasError && (
            <div className="ErrorLabel" key={`ErrorLabel-${name}`}>
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
